import React from "react";
import { preventNegativeValueInDocs } from "_helpers/helpers";

function parseNc4Money(value) {
  if (value === "" || value == null) return null;
  const n = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function fmtNc4Money(n) {
  if (n === null || Number.isNaN(n)) return "";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function nz(n) {
  return n === null || Number.isNaN(n) ? 0 : n;
}

/** N.C. standard deduction for Part II Line 2 from certificate filing status */
function getNcPart2StandardDeduction(form) {
  if (form.filing_status_head_of_household) return 19125;
  if (form.filing_status_married_separate) return 12750;
  if (form.filing_status_married) return 25500;
  if (form.filing_status_single) return 12750;
  return null;
}

/**
 * NC-4 Part II — layout aligned with W-4 page 3 worksheet (row form-row worksheet, stepcounter, price_input).
 */
export default function StateTaxNc4PartII({ form, errors, onFormChange }) {
  const L1 = parseNc4Money(form.nc4_part2_line1);
  const L2 = getNcPart2StandardDeduction(form);
  const L3 =
    L1 !== null && L2 !== null ? Math.max(L1 - L2, 0) : null;

  const L4 = parseNc4Money(form.nc4_part2_line4);
  const L5 = parseNc4Money(form.nc4_part2_line5);
  const L6 =
    L3 !== null ? L3 + nz(L4) + nz(L5) : null;

  const L7 = parseNc4Money(form.nc4_part2_line7);
  const L8 = parseNc4Money(form.nc4_part2_line8);
  const hasL7Input = String(form.nc4_part2_line7 ?? "").trim() !== "";
  const hasL8Input = String(form.nc4_part2_line8 ?? "").trim() !== "";
  const L9sum = nz(L7) + nz(L8);
  const L9display = hasL7Input || hasL8Input ? L9sum : null;

  const L10 =
    L6 !== null ? Math.max(L9sum - L6, 0) : null;
  const L11 = L10 !== null ? Math.floor(L10 / 2500) : null;

  const L12 = parseNc4Money(form.nc4_part2_line12);
  const L13 = L12 !== null ? Math.floor(L12 / 103) : null;

  const L14Raw = String(form.nc4_part2_line14 ?? "").trim();
  const L14Parsed =
    L14Raw === "" ? null : Number.parseInt(L14Raw, 10);
  const L14Num =
    L14Raw === ""
      ? 0
      : Number.isFinite(L14Parsed)
        ? L14Parsed
        : null;

  const L15 =
    L11 !== null && L13 !== null && L14Num !== null
      ? L11 + L13 + L14Num
      : null;

  const L16Raw = String(form.nc4_part2_line16 ?? "").trim();
  const L16Parsed =
    L16Raw === "" ? null : Number.parseInt(L16Raw, 10);
  const L16Num =
    L16Raw === ""
      ? 0
      : Number.isFinite(L16Parsed)
        ? L16Parsed
        : null;

  const L17 =
    L15 !== null && L16Num !== null ? L15 - L16Num : null;

  const readOnlyStyle = {
    pointerEvents: "none",
    background: "#f2f2f2",
  };

  return (
    <div className="row state-tax-nc4-w4 state-tax-nc4-part2">
      <div className="col-md-12">
        <span className="opt_text">OPTIONAL</span>
        <h3 className="mb-2 mt-1">NC-4 Part II</h3>
        <p className="state-tax-nc4-p2-lead">
          Complete this worksheet only if you use Part I and need to figure
          additional withholding allowances. Enter amounts in dollars and cents
          where shown.
        </p>

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>1</strong> Enter an estimate of your N.C. itemized
                  deductions from Page 5, Schedule 1.
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">1</div>
                <input
                  type="number"
                  className={`price_input step_counter ${errors["form.nc4_part2_line1"] ? "error" : ""}`}
                  name="nc4_part2_line1"
                  id="nc4_part2_line1"
                  field="form.nc4_part2_line1"
                  value={form.nc4_part2_line1 ?? ""}
                  onChange={onFormChange}
                  placeholder="- Enter here -"
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={preventNegativeValueInDocs}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>2</strong> Enter the applicable N.C. standard deduction
                  based on your filing status.
                </p>
                <ul className="state-tax-nc4-p2-std-note txt-black">
                  <li>$12,750 if Single</li>
                  <li>$25,500 if Married Filing Jointly or Surviving Spouse</li>
                  <li>$12,750 if Married Filing Separately</li>
                  <li>$19,125 if Head of Household</li>
                </ul>
                <p className="disclaimers dis-notes txt-black mb-0">
                  Line 2 is filled from the filing status you selected on your
                  certificate (page 2). If none is selected, Line 2 stays blank
                  until you update the certificate.
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">2</div>
                <input
                  type="text"
                  className="price_input step_counter"
                  readOnly
                  tabIndex={-1}
                  value={L2 !== null ? fmtNc4Money(L2) : ""}
                  aria-label="Line 2 standard deduction"
                  style={readOnlyStyle}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>3</strong> Subtract Line 2 from Line 1. If Line 1 is
                  less than Line 2, enter ZERO (0).
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">3</div>
                <input
                  type="text"
                  className="price_input step_counter"
                  readOnly
                  tabIndex={-1}
                  value={L3 !== null ? fmtNc4Money(L3) : ""}
                  aria-label="Line 3"
                  style={readOnlyStyle}
                />
              </div>
            </div>
          </div>
        </div>

        {[
          {
            n: 4,
            field: "nc4_part2_line4",
            text: "Enter an estimate of your N.C. child deduction amount from Page 6, Schedule 2.",
          },
          {
            n: 5,
            field: "nc4_part2_line5",
            text: "Enter an estimate of your total federal adjustments to income and N.C. deductions from federal adjusted gross income.",
          },
        ].map(({ n, field, text }) => (
          <div key={field} className="row form-row worksheet pb-2">
            <div className="col">
              <div className="inputs-col">
                <div className="inputs-head">
                  <p className="w-list mb-0">
                    <strong>{n}</strong> {text}
                  </p>
                </div>
                <div className="inputs-items steps_input">
                  <div className="stepcounter">{n}</div>
                  <input
                    type="number"
                    className={`price_input step_counter ${errors[`form.${field}`] ? "error" : ""}`}
                    name={field}
                    id={field}
                    field={`form.${field}`}
                    value={form[field] ?? ""}
                    onChange={onFormChange}
                    placeholder="- Enter here -"
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={preventNegativeValueInDocs}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>6</strong> Add Lines 3, 4, and 5.
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">6</div>
                <input
                  type="text"
                  className="price_input step_counter"
                  readOnly
                  tabIndex={-1}
                  value={L6 !== null ? fmtNc4Money(L6) : ""}
                  aria-label="Line 6"
                  style={readOnlyStyle}
                />
              </div>
            </div>
          </div>
        </div>

        {[
          {
            n: 7,
            field: "nc4_part2_line7",
            text: "Enter an estimate of your nonwage income (such as interest or dividends).",
          },
          {
            n: 8,
            field: "nc4_part2_line8",
            text: "Enter an estimate of your N.C. additions to federal adjusted gross income.",
          },
        ].map(({ n, field, text }) => (
          <div key={field} className="row form-row worksheet pb-2">
            <div className="col">
              <div className="inputs-col">
                <div className="inputs-head">
                  <p className="w-list mb-0">
                    <strong>{n}</strong> {text}
                  </p>
                </div>
                <div className="inputs-items steps_input">
                  <div className="stepcounter">{n}</div>
                  <input
                    type="number"
                    className={`price_input step_counter ${errors[`form.${field}`] ? "error" : ""}`}
                    name={field}
                    id={field}
                    field={`form.${field}`}
                    value={form[field] ?? ""}
                    onChange={onFormChange}
                    placeholder="- Enter here -"
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={preventNegativeValueInDocs}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>9</strong> Add Lines 7 and 8.
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">9</div>
                <input
                  type="text"
                  className="price_input step_counter"
                  readOnly
                  tabIndex={-1}
                  value={L9display !== null ? fmtNc4Money(L9display) : ""}
                  aria-label="Line 9"
                  style={readOnlyStyle}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>10</strong> Subtract Line 6 from Line 9 (Do not enter
                  less than zero).
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">10</div>
                <input
                  type="text"
                  className="price_input step_counter"
                  readOnly
                  tabIndex={-1}
                  value={L10 !== null ? fmtNc4Money(L10) : ""}
                  aria-label="Line 10"
                  style={readOnlyStyle}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>11</strong> Divide the amount on Line 10 by $2,500.
                  Round down to whole number.{" "}
                  <em>
                    Ex. $3,900 ÷ $2,500 = 1.56 rounds down to 1
                  </em>
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">11</div>
                <input
                  type="text"
                  className="price_input step_counter"
                  readOnly
                  tabIndex={-1}
                  value={L11 !== null ? String(L11) : ""}
                  aria-label="Line 11"
                  style={readOnlyStyle}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>12</strong> Enter an estimate of your N.C. tax
                  credits.
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">12</div>
                <input
                  type="number"
                  className={`price_input step_counter ${errors["form.nc4_part2_line12"] ? "error" : ""}`}
                  name="nc4_part2_line12"
                  id="nc4_part2_line12"
                  field="form.nc4_part2_line12"
                  value={form.nc4_part2_line12 ?? ""}
                  onChange={onFormChange}
                  placeholder="- Enter here -"
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={preventNegativeValueInDocs}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>13</strong> Divide the amount on Line 12 by $103. Round
                  down to whole number.{" "}
                  <em>Ex. $200 ÷ $103 = 1.94 rounds down to 1</em>
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">13</div>
                <input
                  type="text"
                  className="price_input step_counter"
                  readOnly
                  tabIndex={-1}
                  value={L13 !== null ? String(L13) : ""}
                  aria-label="Line 13"
                  style={readOnlyStyle}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>14</strong> If filing as Single, Head of Household, or
                  Married Filing Separately, enter zero (0) on this line. If
                  filing as Surviving Spouse, enter 5. If filing as Married
                  Filing Jointly, enter the appropriate number from (a) through
                  (f) below.
                </p>
                <ol className="state-tax-nc4-p2-line14-list txt-black">
                  <li>
                    <strong>(a)</strong> Your spouse expects combined wages and
                    taxable retirement benefits of less than or equal to $250
                    for N.C. purposes, enter <strong>5</strong>. (Taxable
                    retirement benefits do not include: Bailey, certain military
                    retirement, Social Security, and Railroad retirement.)
                  </li>
                  <li>
                    <strong>(b)</strong> More than $250 but less than or equal to
                    $2,750, enter <strong>4</strong>.
                  </li>
                  <li>
                    <strong>(c)</strong> More than $2,750 but less than or equal
                    to $5,250, enter <strong>3</strong>.
                  </li>
                  <li>
                    <strong>(d)</strong> More than $5,250 but less than or equal
                    to $7,750, enter <strong>2</strong>.
                  </li>
                  <li>
                    <strong>(e)</strong> More than $7,750 but less than or equal
                    to $10,250, enter <strong>1</strong>.
                  </li>
                  <li>
                    <strong>(f)</strong> More than $10,250, enter{" "}
                    <strong>0</strong>.
                  </li>
                </ol>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">14</div>
                <input
                  type="number"
                  className={`price_input step_counter ${errors["form.nc4_part2_line14"] ? "error" : ""}`}
                  name="nc4_part2_line14"
                  id="nc4_part2_line14"
                  field="form.nc4_part2_line14"
                  value={form.nc4_part2_line14 ?? ""}
                  onChange={onFormChange}
                  placeholder="- Enter here -"
                  step="1"
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={preventNegativeValueInDocs}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>15</strong> Add Lines 11, 13, and 14, and enter the
                  total here.
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">15</div>
                <input
                  type="text"
                  className="price_input step_counter"
                  readOnly
                  tabIndex={-1}
                  value={L15 !== null ? String(L15) : ""}
                  aria-label="Line 15"
                  style={readOnlyStyle}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row form-row worksheet pb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>16</strong> If you completed this worksheet on the
                  basis of Married Filing Jointly, the total number of allowances
                  determined on Line 15 may be split between you and your spouse,
                  however you choose. Enter the number of allowances from Line
                  15 that your spouse plans to claim.
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">16</div>
                <input
                  type="number"
                  className={`price_input step_counter ${errors["form.nc4_part2_line16"] ? "error" : ""}`}
                  name="nc4_part2_line16"
                  id="nc4_part2_line16"
                  field="form.nc4_part2_line16"
                  value={form.nc4_part2_line16 ?? ""}
                  onChange={onFormChange}
                  placeholder="- Enter here -"
                  step="1"
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={preventNegativeValueInDocs}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row form-row worksheet pb-2 mb-2">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                <p className="w-list mb-0">
                  <strong>17</strong> Subtract Line 16 from Line 15 and enter the
                  total number of allowances here and on Line 1 of your Form
                  NC-4, Employee&apos;s Withholding Allowance Certificate.
                </p>
              </div>
              <div className="inputs-items steps_input">
                <div className="stepcounter">17</div>
                <input
                  type="text"
                  className="price_input step_counter"
                  readOnly
                  tabIndex={-1}
                  value={L17 !== null ? String(L17) : ""}
                  aria-label="Line 17"
                  style={readOnlyStyle}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row form-row no_border no_padding">
          <div className="col">
            <div className="disclaimers dis-notes txt-black">
              <strong>
                Complete Part II only if it applies to you; otherwise you may
                skip this page.
              </strong>{" "}
              See Part I (previous page) for when to use Line 1 allowances of
              zero or five. Confirm your entries on the next page (Schedule 1).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
